var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var MultiPortPipeConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonConverter, JsonDiscriminatorValue, JsonSchema, MapConverter } from '@symon-ai/wisepipe-serialize';
import { EdgeElement } from './edgeElement';
import { ExportConfig } from './exportConfig';
import { FileConfig } from './fileConfig';
import { Graph } from './graph';
import { MultiPortPipeInput } from './multiPortPipeInput';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
const schema = {
    $id: 'urn:wisepipe-common-pipe:MultiPortPipeConfig',
    type: 'object',
    properties: {
        pipeID: { type: 'string', uid: 10 },
        inputMap: {
            type: 'object',
            additionalProperties: { $ref: 'urn:wisepipe-common-pipe:MultiPortPipeInput' }
        },
        outputMap: {
            type: 'object',
            additionalProperties: { type: 'string' }
        }
    },
    required: ['pipeID', 'inputMap', 'outputMap']
};
let MultiPortPipeConfig = MultiPortPipeConfig_1 = class MultiPortPipeConfig extends ToolConfig {
    constructor(pipeID, inputMap, outputMap, customFormats) {
        super(ToolType.MULTI_PORT_PIPE, customFormats);
        this.pipeID = pipeID;
        this.inputMap = inputMap;
        this.outputMap = outputMap;
        JSON.validate(this, MultiPortPipeConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    multiIns(inScope) {
        return [...inScope.names];
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return MultiPortPipeConfig_1.ins;
    }
    get outs() {
        return Array.from(this.outputMap.values());
    }
    // still need static ins for info().
    // use instance method multiIns() to get actual input port names
    static get ins() {
        return ['multi'];
    }
    static get outs() {
        return ['multi'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.PIPE.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.DATA], this.ins, this.outs, ['pipe'], true);
    }
    getOutput(iConfigHelper, ins) {
        const dependeeIns = ins.dependee.inputNodes();
        const dependeeOuts = ins.dependee.outputNodes();
        // all dependeeIns are mapped to a new datasource. overwrite the dependeeIn's scope
        dependeeIns.forEach((dependeeIn) => {
            const inputConfig = this.inputMap.get(dependeeIn.elementID);
            const port = this.getPortName(inputConfig);
            const tsb = new ToolScopeBuilder(ins);
            const source = ins.schemas.at(port);
            // create renaming map. where dependant col names are old names, and dependee col names are new names
            const renameMap = inputConfig.columnMappings.reduce((result, { dependantColumnName, dependeeColumnName }) => {
                if (!result[dependantColumnName]) {
                    result[dependantColumnName] = [];
                }
                result[dependantColumnName].push(dependeeColumnName);
                return result;
            }, {});
            const exp = tsb.export(FileConfig.out);
            source.forEach((s) => {
                // cols not in renameMap are dropped
                if (renameMap[s.name]) {
                    // cols in renameMap are renamed. if theres more than one in array, make multiple copies
                    renameMap[s.name].forEach((newName) => {
                        exp.copy(port, s, false, true, undefined, newName);
                    });
                }
            });
            ins.dependeeMeta.setOutput(dependeeIn.elementID, tsb.build());
        });
        // get scope for each output
        const outs = dependeeOuts.map((output, index) => {
            const outPort = this.outputMap.get(output.elementID);
            const outputIns = ins.dependeeMeta.getMetadata(output.elementID).ins;
            const tsb = new ToolScopeBuilder(outputIns);
            tsb.setPipeID(ins.pipeID).setDependants(ins.dependants);
            tsb.export(outPort).copyAll(ExportConfig.in).initializeColumnFormats().setColumnFormats(this.customFormats);
            return tsb.build();
        });
        const tsb = new ToolScopeBuilder(...outs);
        return tsb.exportAll().build();
    }
    isValidForCalc(iConfigHelper, scope) {
        if (scope.pipeID === this.pipeID || scope.dependants.some((d) => d.pipeID === this.pipeID)) {
            return ToolValidationState.CYCLE;
        }
        const dependee = scope.dependee;
        const dependeeMeta = scope.dependeeMeta;
        if (!dependee || !dependeeMeta) {
            return ToolValidationState.BAD_METADATA;
        }
        const inputNames = scope.names; // port names are elementIDs of source elements
        const dependeeIns = dependee.inputNodes();
        const dependeeOuts = dependee.outputNodes();
        if (dependeeOuts.length === 0) {
            return ToolValidationState.BAD_METADATA;
        }
        // check that all dependeeIns exists as a key in inputMap
        if (!dependeeIns.every((dependeeIn) => this.inputMap.has(dependeeIn.elementID))) {
            return ToolValidationState.MULTI_PORT_UNMAPPED_INPUT;
        }
        // check that all ports in inputMap config is consistent with ports in metadata
        // if previousPipeToolOutputPort is defined, port name is {port}_{previousPipeToolOutputPort}
        const portsInConfig = Array.from(this.inputMap.values()).map((input) => {
            if (input.previousPipeToolOutputPort)
                return `${input.port}_${input.previousPipeToolOutputPort}`;
            return input.port;
        });
        if (!portsInConfig.every((portInConfig) => inputNames.has(portInConfig))) {
            return ToolValidationState.MULTI_PORT_MAPPED_INPUT_MISSING;
        }
        const badColumnMappings = this.getDataForBadColumnMappings(scope);
        if (badColumnMappings?.length) {
            return ToolValidationState.MULTI_PORT_MAPPED_COLUMN_INVALID;
        }
        // all dependeeIns are mapped to a new datasource. overwrite the dependeeIn's scope
        dependeeIns.forEach((dependeeIn) => {
            const inputConfig = this.inputMap.get(dependeeIn.elementID);
            const port = this.getPortName(inputConfig);
            const tsb = new ToolScopeBuilder(scope);
            const source = scope.schemas.at(port);
            // create renaming map. where dependant col names are old names, and dependee col names are new names
            const renameMap = inputConfig.columnMappings.reduce((result, { dependantColumnName, dependeeColumnName }) => {
                if (!result[dependantColumnName]) {
                    result[dependantColumnName] = [];
                }
                result[dependantColumnName].push(dependeeColumnName);
                return result;
            }, {});
            const exp = tsb.export(FileConfig.out);
            source.forEach((s) => {
                // cols not in renameMap are dropped
                if (renameMap[s.name]) {
                    // cols in renameMap are renamed. if theres more than one in array, make multiple copies
                    renameMap[s.name].forEach((newName) => {
                        exp.copy(port, s, false, true, undefined, newName);
                    });
                }
            });
            dependeeMeta.setOutput(dependeeIn.elementID, tsb.build());
        });
        // validate each output of the inner pipe
        for (const output of dependeeOuts) {
            const subGraph = new Graph([...dependee.ancestors(output.elementID)]);
            for (const n of subGraph.getComputeOrder()) {
                const ins = dependeeMeta.getMetadata(n.elementID).ins;
                if (!n.config) {
                    return ToolValidationState.EMPTY;
                }
                const state = n.config.isValidForRun(iConfigHelper, ins, false);
                if (state !== ToolValidationState.VALID) {
                    return state;
                }
            }
        }
        // check for duplicate output ports
        const outputPorts = Array.from(this.outputMap.values());
        if (new Set(outputPorts).size !== outputPorts.length) {
            return ToolValidationState.INVALID;
        }
        // check that each output port is linked to a valid export node from the inner pipe
        // and that each export node in the inner pipe is linked to an output port in the outputMap
        const dependeeOutsIDs = new Set(dependeeOuts.map((dependeeOut) => dependeeOut.elementID));
        const exportNodeElementIDs = Array.from(this.outputMap.keys());
        if (dependeeOutsIDs.size !== exportNodeElementIDs.length ||
            !exportNodeElementIDs.every((exportNodeElementID) => dependeeOutsIDs.has(exportNodeElementID))) {
            return ToolValidationState.MULTI_PORT_UNMAPPED_OUTPUT;
        }
        return ToolValidationState.VALID;
    }
    isValidForRun(iConfigHelper, scope, pass) {
        return this.isValidForCalc(iConfigHelper, scope);
    }
    getPortName(input) {
        const port = input.previousPipeToolOutputPort ? `${input.port}_${input.previousPipeToolOutputPort}` : input.port;
        return port;
    }
    getDataForBadColumnMappings(scope) {
        const dependee = scope.dependee;
        const dependeeMeta = scope.dependeeMeta;
        if (!dependee || !dependeeMeta)
            return undefined;
        const dependeeIns = dependee.inputNodes();
        const result = [];
        // check that all dependee columns are mapped properly to an incoming column
        for (const dependeeIn of dependeeIns) {
            const dependeeInCols = dependeeMeta.getMetadata(dependeeIn.elementID).ins.data.at(dependeeIn.config.fileID) ?? [];
            const inputConfig = this.inputMap.get(dependeeIn.elementID);
            for (const dependeeInCol of dependeeInCols) {
                const mappedOccurances = inputConfig.columnMappings.filter((c) => c.dependeeColumnName === dependeeInCol.name);
                if (mappedOccurances.length !== 1 || !mappedOccurances[0].dependantColumnName) {
                    // column should be mapped exactly once and mapped to a non-empty string
                    result.push(inputConfig);
                }
                else {
                    // check that dependee col type is consistent with mapped dependant col type
                    const port = this.getPortName(inputConfig);
                    const dependantCol = scope.schemas.at(port)?.find((c) => c.name === mappedOccurances[0].dependantColumnName);
                    if (!dependantCol) {
                        // mapped column not found
                        result.push(inputConfig);
                    }
                    else if (dependantCol.type !== dependeeInCol.type) {
                        // column types don't match
                        result.push(inputConfig);
                    }
                }
            }
        }
        return result;
    }
    /**
     * special edge case: There can only be one edge between two elements (UI restriction). However if an edge is between a multi-output tool and a multi-input tool,
     * there may be multiple connections represented by one edge. This function returns all connections represented by one edge.
     */
    getAllConnectionsfromEdge(edge) {
        // multiEdge here means multiple connections between ports of source and target nodes although there is only one physical edge in graph
        const multiEdges = Array.from(this.inputMap.values()).filter((input) => input.port === edge.tarPort && input.previousPipeToolOutputPort);
        const virtualEdgeElements = multiEdges.map((e) => {
            return new EdgeElement('A'.repeat(14), // just a temporary fake edge so ID doesn't matter
            edge.srcElementID, e.previousPipeToolOutputPort, edge.tarElementID, this.getPortName(e));
        });
        return virtualEdgeElements;
    }
};
__decorate([
    JsonConverter(new MapConverter(MultiPortPipeInput))
], MultiPortPipeConfig.prototype, "inputMap", void 0);
__decorate([
    JsonConverter(new MapConverter())
], MultiPortPipeConfig.prototype, "outputMap", void 0);
MultiPortPipeConfig = MultiPortPipeConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.MULTI_PORT_PIPE)
], MultiPortPipeConfig);
export { MultiPortPipeConfig };
//# sourceMappingURL=multiPortPipeConfig.js.map